import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Define translations
const podcastTranslations = {
  en: {
    pageTitle: "Podcast Episodes",
    pageSubtitle: "Listen to Barkin Sayin discuss entrepreneurship, technology, and innovation across various podcast appearances.",
    allEpisodes: "All Episodes",
    loadingVideo: "Loading video...",
    nowPlaying: "NOW PLAYING",
    podcasts: [
      {
        title: "How to Manage Your Time Effectively",
        description: "Barkin shares valuable insights on time management strategies for entrepreneurs and business professionals."
      },
      {
        title: "Algorithmic Trading Strategies",
        description: "Exploring powerful algorithmic strategies for modern financial markets."
      },
      {
        title: "Innovation in FinTech",
        description: "Discussing the latest innovations and disruptions in financial technology."
      },
      {
        title: "Leadership in Digital Transformation",
        description: "How effective leadership drives successful digital transformation initiatives."
      },
      {
        title: "Global Market Analysis",
        description: "Analyzing global market trends and identifying investment opportunities."
      },
      {
        title: "Sustainable Business Growth",
        description: "Strategies for building businesses that achieve long-term sustainable growth."
      },
      {
        title: "AI in Financial Services",
        description: "How artificial intelligence is revolutionizing the financial services industry."
      }
    ]
  },
  tr: {
    pageTitle: "Podcast Bölümleri",
    pageSubtitle: "Barkın Sayın'ın çeşitli podcast yayınlarında girişimcilik, teknoloji ve inovasyon hakkındaki konuşmalarını dinleyin.",
    allEpisodes: "Tüm Bölümler",
    loadingVideo: "Video yükleniyor...",
    nowPlaying: "ŞİMDİ OYNATILIYOR",
    podcasts: [
      {
        title: "Zamanınızı Etkili Bir Şekilde Nasıl Yönetirsiniz",
        description: "Barkin, girişimciler ve iş profesyonelleri için zaman yönetimi stratejileri hakkında değerli bilgiler paylaşıyor."
      },
      {
        title: "Algoritmik Ticaret Stratejileri",
        description: "Modern finansal piyasalar için güçlü algoritmik stratejileri keşfetmek."
      },
      {
        title: "FinTech'te İnovasyon",
        description: "Finansal teknolojideki en son yenilikler ve değişimleri tartışmak."
      },
      {
        title: "Dijital Dönüşümde Liderlik",
        description: "Etkili liderliğin başarılı dijital dönüşüm girişimlerini nasıl yönlendirdiği."
      },
      {
        title: "Küresel Pazar Analizi",
        description: "Küresel pazar trendlerini analiz etmek ve yatırım fırsatlarını belirlemek."
      },
      {
        title: "Sürdürülebilir İş Büyümesi",
        description: "Uzun vadeli sürdürülebilir büyüme sağlayan işletmeler kurma stratejileri."
      },
      {
        title: "Finansal Hizmetlerde Yapay Zeka",
        description: "Yapay zekanın finansal hizmetler sektörünü nasıl dönüştürdüğü."
      }
    ]
  },
  fr: {
    pageTitle: "Épisodes du Podcast",
    pageSubtitle: "Écoutez Barkin Sayin discuter de l'entrepreneuriat, de la technologie et de l'innovation à travers diverses apparitions de podcast.",
    allEpisodes: "Tous les Épisodes",
    loadingVideo: "Chargement de la vidéo...",
    nowPlaying: "EN COURS DE LECTURE",
    podcasts: [
      {
        title: "Comment Gérer Efficacement Votre Temps",
        description: "Barkin partage des informations précieuses sur les stratégies de gestion du temps pour les entrepreneurs et les professionnels."
      },
      {
        title: "Le Pouvoir du Développement Personnel",
        description: "Découvrez les techniques de croissance personnelle et les changements d'état d'esprit qui peuvent transformer votre carrière et votre vie."
      },
      {
        title: "Créer une Startup Technologique sur le Marché Actuel",
        description: "Découvrez les stratégies clés pour créer et développer une startup technologique réussie dans le paysage concurrentiel actuel."
      },
      {
        title: "La Route vers le Succès",
        description: "Barkin discute des défis et des triomphes du parcours entrepreneurial et comment surmonter les obstacles."
      },
      {
        title: "Compétences de Leadership pour le Monde des Affaires Moderne",
        description: "Explorez les principes essentiels de leadership qui peuvent vous aider à guider les équipes et les organisations vers le succès."
      },
      {
        title: "Innovation et Disruption dans les Affaires",
        description: "Apprenez à favoriser l'innovation et à créer des modèles d'affaires disruptifs qui peuvent transformer les industries."
      },
      {
        title: "Créer une Stratégie d'Affaires Réussie",
        description: "Barkin présente le cadre pour développer des stratégies d'affaires qui stimulent la croissance et l'avantage concurrentiel."
      }
    ]
  },
  es: {
    pageTitle: "Episodios de Podcast",
    pageSubtitle: "Escuche a Barkin Sayin hablar sobre emprendimiento, tecnología e innovación en varias apariciones de podcast.",
    allEpisodes: "Todos los Episodios",
    loadingVideo: "Cargando video...",
    nowPlaying: "REPRODUCIENDO AHORA",
    podcasts: [
      {
        title: "Cómo Administrar su Tiempo Efectivamente",
        description: "Barkin comparte información valiosa sobre estrategias de gestión del tiempo para emprendedores y profesionales de negocios."
      },
      {
        title: "El Poder del Desarrollo Personal",
        description: "Aprenda sobre técnicas de crecimiento personal y cambios de mentalidad que pueden transformar su carrera y su vida."
      },
      {
        title: "Construyendo una Startup Tecnológica en el Mercado Actual",
        description: "Descubra estrategias clave para construir y escalar una startup tecnológica exitosa en el panorama competitivo actual."
      },
      {
        title: "El Camino al Éxito",
        description: "Barkin discute los desafíos y triunfos en el viaje emprendedor y cómo superar obstáculos."
      },
      {
        title: "Habilidades de Liderazgo para el Mundo Empresarial Moderno",
        description: "Explore principios esenciales de liderazgo que pueden ayudarlo a guiar equipos y organizaciones hacia el éxito."
      },
      {
        title: "Innovación y Disrupción en los Negocios",
        description: "Aprenda cómo fomentar la innovación y crear modelos de negocio disruptivos que pueden transformar industrias."
      },
      {
        title: "Creando una Estrategia de Negocio Exitosa",
        description: "Barkin describe el marco para desarrollar estrategias de negocio que impulsan el crecimiento y la ventaja competitiva."
      }
    ]
  },
  ar: {
    pageTitle: "حلقات البودكاست",
    pageSubtitle: "استمع إلى باركين سايين وهو يناقش ريادة الأعمال والتكنولوجيا والابتكار عبر ظهوره في مختلف البودكاست.",
    allEpisodes: "جميع الحلقات",
    loadingVideo: "جاري تحميل الفيديو...",
    nowPlaying: "قيد التشغيل الآن",
    podcasts: [
      {
        title: "كيفية إدارة وقتك بفعالية",
        description: "يشارك باركين رؤى قيمة حول استراتيجيات إدارة الوقت لرواد الأعمال والمهنيين في مجال الأعمال."
      },
      {
        title: "قوة التطوير الشخصي",
        description: "تعلم عن تقنيات النمو الشخصي وتحولات العقلية التي يمكن أن تغير حياتك المهنية وحياتك."
      },
      {
        title: "بناء شركة ناشئة تقنية في سوق اليوم",
        description: "اكتشف الاستراتيجيات الرئيسية لبناء وتوسيع شركة ناشئة تقنية ناجحة في المشهد التنافسي الحالي."
      },
      {
        title: "الطريق إلى النجاح",
        description: "يناقش باركين التحديات والانتصارات في رحلة ريادة الأعمال وكيفية التغلب على العقبات."
      },
      {
        title: "مهارات القيادة لعالم الأعمال الحديث",
        description: "استكشف مبادئ القيادة الأساسية التي يمكن أن تساعدك في توجيه الفرق والمنظمات نحو النجاح."
      },
      {
        title: "الابتكار والتحول في الأعمال",
        description: "تعلم كيفية تعزيز الابتكار وإنشاء نماذج أعمال تحويلية يمكنها تغيير الصناعات."
      },
      {
        title: "إنشاء استراتيجية أعمال ناجحة",
        description: "يحدد باركين إطار تطوير استراتيجيات الأعمال التي تدفع النمو والميزة التنافسية."
      }
    ]
  }
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      when: "beforeChildren", 
      staggerChildren: 0.05,
      duration: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

// Styled Components
const PageSection = styled.section`
  min-height: 100vh;
  padding-top: 80px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(to bottom, rgba(212, 175, 55, 0.05), transparent);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(to top, rgba(212, 175, 55, 0.05), transparent);
    z-index: 0;
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.03;
  overflow: hidden;
  z-index: 0;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 20% 50%, rgba(0, 0, 0, 0.2) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(212, 175, 55, 0.2) 0%, transparent 50%);
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  z-index: 0;
`;

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 40px;
`;

const PageHeader = styled(motion.div)`
  margin-bottom: 40px;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3.2rem;
  color: #111;
  margin-bottom: 20px;
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: #D4AF37;
    margin: 15px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #444;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  text-align: center;
`;

const PlayerSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  margin-bottom: 60px;
  position: relative;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const VideoContainer = styled.div`
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 0;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(212, 175, 55, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: 0;
    padding: 2px;
    background: linear-gradient(
      45deg, 
      transparent 0%, 
      rgba(0, 0, 0, 0.3) 25%, 
      rgba(212, 175, 55, 0.3) 50%,
      rgba(0, 0, 0, 0.3) 75%,
      transparent 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    animation: borderRotate 8s linear infinite;
  }
  
  @keyframes borderRotate {
    0% { background-position: 0% 0%; }
    100% { background-position: 200% 0%; }
  }
`;

const VideoFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  position: relative;
  z-index: 1;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, transparent 5%, transparent 95%, rgba(0, 0, 0, 0.1) 100%);
  }
`;

const PlaylistContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0;
  border: 1px solid rgba(212, 175, 55, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 1100px) {
    max-height: 400px;
  }
`;

const PlaylistHeader = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
  position: sticky;
  top: 0;
  z-index: 5;
`;

const PlaylistTitle = styled.h3`
  margin: 0;
  font-size: 1.4rem;
  color: #111;
  font-weight: 500;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: #D4AF37;
    border-radius: 50%;
    margin-right: 15px;
    box-shadow: 0 0 15px rgba(212, 175, 55, 0.5);
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 0.6; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.1); }
    100% { opacity: 0.6; transform: scale(0.9); }
  }
`;

const PlaylistBody = styled.div`
  padding: 0;
  overflow-y: auto;
  height: 400px;
  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
  }
`;

const PlaylistGrid = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const PlaylistItem = styled(motion.div)<{ active: boolean }>`
  border-radius: 0;
  background: ${props => props.active ? 'rgba(212, 175, 55, 0.05)' : 'rgba(255, 255, 255, 0.8)'};
  border: 1px solid ${props => props.active ? 'rgba(212, 175, 55, 0.15)' : 'rgba(200, 200, 200, 0.3)'};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  cursor: pointer;
  position: relative;
  height: 90px;
  margin: 5px 15px;
  
  &:first-child {
    margin-top: 15px;
  }
  
  &:last-child {
    margin-bottom: 15px;
  }
  
  ${props => props.active && `
    box-shadow: 0 0 20px rgba(212, 175, 55, 0.1);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: #D4AF37;
      box-shadow: 0 0 10px rgba(212, 175, 55, 0.3);
    }
  `}
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    background: ${props => props.active ? 'rgba(212, 175, 55, 0.08)' : 'rgba(255, 255, 255, 0.95)'};
  }
`;

const ItemThumbnail = styled.div<{ image: string }>`
  width: 120px;
  min-width: 120px;
  height: 68px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  margin: 10px;
  border-radius: 0;
  
  @media (max-width: 600px) {
    width: 100px;
    min-width: 100px;
    height: 56px;
  }
`;

const PlayIcon = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  svg {
    width: 24px;
    height: 24px;
    fill: white;
    filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.5));
  }
  
  ${PlaylistItem}:hover & {
    opacity: 1;
  }
`;

const ItemInfo = styled.div`
  padding: 10px 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;

const ItemTitle = styled.h4`
  margin: 0 0 5px;
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ItemDescription = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NowPlaying = styled(motion.span)`
  display: inline-block;
  font-size: 0.65rem;
  padding: 3px 6px;
  border-radius: 4px;
  background: #D4AF37;
  color: white;
  margin-right: 6px;
  font-weight: 600;
  box-shadow: 0 0 10px rgba(212, 175, 55, 0.3);
  letter-spacing: 0.5px;
`;

const LoaderOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  z-index: 5;
  border-radius: 0;
`;

const LoaderSpinnerContainer = styled(motion.div)`
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
`;

const LoaderSpinner = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top: 3px solid #D4AF37;
  border-right: 3px solid transparent;
  
  &:nth-child(1) {
    animation: spin 1.5s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoaderText = styled(motion.p)`
  color: #fff;
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
  letter-spacing: 1px;
`;

const PodcastsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Podcasts: React.FC = () => {
  const { language } = useLanguage();
  const [activeVideoId, setActiveVideoId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLDivElement>(null);

  // Get text based on the current language
  const getText = (key: keyof typeof podcastTranslations.en): any => {
    if (language === 'en' || language === 'tr' || language === 'fr' || language === 'es' || language === 'ar') {
      return podcastTranslations[language as keyof typeof podcastTranslations][key];
    }
    return podcastTranslations.en[key];
  };

  // Helper function to extract YouTube video ID
  const getYouTubeID = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };
  
  // Extract YouTube embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    const videoId = getYouTubeID(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&origin=${window.location.origin}` : '';
  };

  // Raw podcast data with YouTube URLs and titles
  const rawPodcasts = [
    {
      url: "https://youtu.be/rSjHyvtESZ4?si=Rt__z-5PmqLsta7n",
      title: "Bu Kadar Rahatlık Bize İyi Gelmedi",
      description: "Bugün her şeye çok hızlı ulaşıyoruz. Yemek, bilgi, eğlence, alışveriş, iletişim… Hayat kolaylaştı ama bu kolaylık bizi her zaman daha güçlü yapmadı. Sabır azaldı, dikkat dağıldı, tahammül düştü ve insanlar en küçük rahatsızlıkta kaçacak bir yer aramaya başladı.\n\nBu videoda konforun insanı nasıl zayıflattığını, disiplinin neden artık daha değerli hale geldiğini ve yeni dünyada dayanıklı kalmanın neden gerçek bir avantaj olduğunu konuşuyorum. Kendi hayatımdan, spordan, iş disiplininden ve şu an launch aşamasına gelen Trade on tv mobile ile newsGather projelerinden örneklerle anlatıyorum."
    },
    {
      url: "https://youtu.be/PtUo5-881so?si=ESPRLy3tnlHOKit-",
      title: "Türkiye Yeni Bir Finans Merkezi mi Oluyor? 20 Yıllık Vergi Muafiyeti",
      description: "Türkiye'nin açıkladığı 20 yıllık vergi avantajı ne anlama geliyor? Bu hamle yabancı yatırımcıyı, yurt dışındaki Türk iş insanlarını ve global sermayeyi Türkiye'ye çekebilir mi? Bu videoda konuyu iş insanı bakışıyla değerlendiriyorum."
    },
    {
      url: "https://www.youtube.com/watch?v=30LYWXW7jt8",
      title: "HER ŞEYİMİ KAYBETSEM - ilk 72 Saat nasıl geçer?",
      description: "Bugün her şeyimi kaybetsem, panik yapmam. İlk 72 saatte tabloyu net görür, gereksizi keser, hızlı nakit akışını arar ve doğru fırsatları seçerim. Bu videoda, yeniden ayağa kalkmanın duygusal değil, net ve stratejik tarafını anlatıyorum. İyi seyirler."
    },
    {
      url: "https://www.youtube.com/watch?v=HzuCDIEdKjE",
      title: "Dışarıdan Havalı, İçeriden Ağır - BARKIN SAYIN",
      description: "İnsanlar sonucu görüyor, ritmi görmüyor. Özgürlüğü görüyor, bedelini görmüyor. Bu videoda dışarıdan iyi görünen bir hayatın içeride gerçekten nasıl taşındığını net şekilde konuşuyorum."
    },
    {
      url: "https://youtu.be/NdWT17H9ZDw?si=_TiW0MZTgO--Ac6K",
      title: "Adamı kokusundan anlarım",
      description: "Bir insanı anlamak bazen sanıldığı kadar zor değil. Para, görünüş ve gösteriş ilk bakışta dikkat çekebilir ama gerçek seviye tavırda, üslupta, dinlemede ve karşısındakine davranışta ortaya çıkar. Bu videoda, insanların ilk dakikalarda kendini nasıl ele verdiğini net örneklerle anlatıyorum."
    },
    {
      url: "https://youtu.be/KzNUhi5kV20?si=ec5KE40CyYriqR4Q",
      title: "Ben Neden Artık Kimseyi Dinlemiyorum?",
      description: "Dubai'den başlayan son gerilimleri, Türkiye ve Avrupa'ya olası etkilerini ve asıl önemli tarafı konuşuyorum: insanların artık haberlerden değil, sürekli gürültüden yorulmasını. Bu videoda kalabalığın peşinden gitmenin neden insanı aynı hayata sürüklediğini, net kalmanın neden bugün en büyük güç olduğunu ve kendi ritmini korumanın neden her zamankinden daha önemli hale geldiğini anlatıyorum."
    },
    {
      url: "https://youtu.be/m7n0zqzEPvM?si=EOI15xR-Pfy6iBbt",
      title: "PANİK TUZAĞI | GÜNDEM SENİ YÖNETMESİN",
      description: "Bugün herkes Epstein, kriz, savaş ve altın konuşuyor. Bu bilgi akışı seni ya panikle donduruyor ya da uyuşturuyor. Bu videoda iki şeyi net anlatıyorum: \"gündem bağımlılığının\" seni nasıl kontrol ettiğini. Sonra da basit ama sert bir plan veriyorum: bilgi diyeti, rutin, disiplin ve üretim. Gündem değişir; sen değişmezsen hep aynı döngü."
    },
    {
      url: "https://youtu.be/bocCmmVbS8M?si=ke1RiUVfLXX527-m",
      title: "Unternehmer in Deutschland: Das Problem ist nicht Geld | Familie & Freunde",
      description: "Almanya'da girişimcilik üzerine derinlemesine bir analiz. Para değil, asıl sorunun ne olduğunu ve aile ile arkadaşların rolünü tartışıyoruz."
    },
    {
      url: "https://youtu.be/USU3_P8POQs?si=z6rBrMUi5dnLpJZq",
      title: "ABD Maduro'yu Aldı mı? | Türkiye'de Olur mu? (Gerçeği Konuşalım)",
      description: "Venezuela lideri Maduro'nun durumu ve Türkiye'de benzer bir senaryonun mümkün olup olmadığı üzerine gerçekçi bir değerlendirme."
    },
    {
      url: "https://youtu.be/vf6CksPup7A?si=5zrnNAhfp9V8uGpz",
      title: "Mr. Balmain Bitti: Neden Markasız Hayata Geçtim?",
      description: "Lüks markalardan vazgeçip markasız bir hayata geçiş hikayesi. Neden bu kararı aldım ve hayatımda ne değişti?"
    },
    {
      url: "https://www.youtube.com/watch?v=2fpo8ounZ6A",
      title: "Türkiye'den Neden Global Marka Çıkmıyor? | Barkın Sayın Gerçekleri Konuşuyor",
      description: "Türkiye'de üretim var, girişim var, zekâ var. Ama neden hâlâ bir \"Apple\", \"Tesla\" ya da \"Samsung\" çıkaramadık? Getir, Trendyol, Peak, LC Waikiki… Evet başarılı şirketler, ama dünya çapında bir marka olabilmek için ne eksik?"
    },
    {
      url: "https://www.youtube.com/watch?v=WnAZYJUkoOQ",
      title: "13x Dünya Şampiyonu \"Kayhan Gülmez\" ile Boks Antrenmanı 🥊🔥",
      description: "Bu videoda 13 kez Dünya Şampiyonu değerli hocam Kayhan Gülmez ile birlikte boks antrenmanı yaptık. Gerçek bir şampiyondan teknikler, disiplin ve odaklanma üzerine bilgiler paylaşıyor. Boks sadece bir spor değil; sınırlarını zorlamak, mücadele etmek ve güç kazanmak demek."
    },
    {
      url: "https://www.youtube.com/watch?v=Voy91dMEhDw",
      title: "Iran-Israel: Kein Krieg, sondern WAS???",
      description: "In diesem Video spreche ich über die wahren Hintergründe des Iran-Israel-Konflikts. Warum hat die Eskalation plötzlich aufgehört? Wer gewinnt politisch und wirtschaftlich – und wer wird nur benutzt? Ich analysiere, was die Türkei, die USA, die arabischen Länder, Großbritannien und Russland wirklich davon haben – und warum du der offiziellen Version nicht blind glauben solltest."
    },
    {
      url: "https://www.youtube.com/watch?v=qmRBGJF7ifA",
      title: "Ucuzken Kimse Umursamadı… Şimdi Değerlendi",
      description: "Bazı şeyler hayatta sadece bir kez gelir. Ucuzken değersiz sanılır, ama değerlendiğinde herkes peşinden koşar."
    },
    {
      url: "https://www.youtube.com/watch?v=HtagpSrGrZc",
      title: "Glory 100 Rotterdam VLOG | Bahram, Rico, Donovan, Jamal Ben Saddik, Tariq , Petch – Ringin Ötesi",
      description: "Rotterdam'da düzenlenen GLORY 100 gecesi, sadece ringde değil, zihnimde de büyük bir mücadeleydi."
    },
    {
      url: "https://www.youtube.com/watch?v=E91Oniho_vo",
      title: "10.000$ Uçakta Çalındı… Ama Asıl Şok, Dubai'deki O Masada Yaşandı",
      description: "2021'in sonunda, gece bir THY business uçuşunda derin bir uykuya daldım. Gözümü Dubai'de açtığımda ise her şey değişmişti."
    },
    {
      url: "https://www.youtube.com/watch?v=nv39EvVHs-8&t=64s&pp=0gcJCY0JAYcqIYzv",
      title: "Barkin Sayin: Smart TV Applications and Financial Technology",
      description: "An in-depth discussion on Smart TV applications and their integration with financial technology solutions."
    },
    {
      url: "https://www.youtube.com/watch?v=aIRAEluhXjY",
      title: "Barkin Sayin: Fintech Innovation and Entrepreneurship",
      description: "In this podcast, Barkin Sayin discusses the latest trends in fintech innovation and entrepreneurship."
    },
    {
      url: "https://www.youtube.com/watch?v=ljdZmEtnlfA",
      title: "The Future of Digital Finance with Barkin Sayin",
      description: "Barkin Sayin shares insights on the future of digital finance and how technology is transforming the industry."
    },
    {
      url: "https://www.youtube.com/watch?v=FI_dBVqr-gE",
      title: "Algorithmic Trading Strategies - Expert Talk",
      description: "A deep dive into algorithmic trading strategies with financial technology expert Barkin Sayin."
    },
    {
      url: "https://www.youtube.com/watch?v=3IIIKPxZBqA",
      title: "How to Manage Your Time Effectively - Barkin Sayin",
      description: "Barkin shares valuable insights on time management strategies for entrepreneurs and business professionals."
    },
    {
      url: "https://www.youtube.com/watch?v=r3WOJY8Jpsg",
      title: "Algorithmic Trading Strategies for Modern Markets",
      description: "Exploring powerful algorithmic strategies for modern financial markets with Barkin Sayin."
    },
    {
      url: "https://www.youtube.com/watch?v=sk6mckKg2SE",
      title: "Innovation in FinTech - Industry Disruption",
      description: "Discussing the latest innovations and disruptions in financial technology with industry expert Barkin Sayin."
    },
    {
      url: "https://www.youtube.com/watch?v=ilweg39nhPo",
      title: "Leadership in Digital Transformation",
      description: "How effective leadership drives successful digital transformation initiatives in the modern business landscape."
    },
    {
      url: "https://www.youtube.com/watch?v=LwazjcbU0lI",
      title: "Global Market Analysis with Barkin Sayin",
      description: "Analyzing global market trends and identifying investment opportunities in today's volatile economy."
    },
    {
      url: "https://www.youtube.com/watch?v=C1Qq9Ot6CmI",
      title: "Sustainable Business Growth Strategies",
      description: "Strategies for building businesses that achieve long-term sustainable growth in competitive markets."
    },
    {
      url: "https://www.youtube.com/watch?v=PbHGCnT4mZk",
      title: "AI in Financial Services - Revolution",
      description: "How artificial intelligence is revolutionizing the financial services industry and creating new opportunities."
    },
    {
      url: "https://www.youtube.com/watch?v=fbG2QafgZJk",
      title: "Blockchain Technology and Its Applications",
      description: "Exploring the potential of blockchain technology and its applications in various industries."
    },
    {
      url: "https://www.youtube.com/watch?v=h5kM_m4dovw",
      title: "Investment Strategies for Uncertain Times",
      description: "Expert advice on investment strategies that can help navigate uncertain economic conditions."
    },
    {
      url: "https://www.youtube.com/watch?v=9tzO4a3C_x0",
      title: "The Power of Data Analytics in Business",
      description: "How data analytics can drive business decisions and create competitive advantages in the market."
    },
    {
      url: "https://www.youtube.com/watch?v=nr6NAm_Cmn0",
      title: "Entrepreneurship Journey: Lessons Learned",
      description: "Barkin Sayin shares valuable lessons from his entrepreneurship journey and experiences in the business world."
    },
    {
      url: "https://www.youtube.com/watch?v=ipkMImpG09k",
      title: "Digital Transformation in Traditional Industries",
      description: "How traditional industries are embracing digital transformation to stay competitive in the modern era."
    },
    {
      url: "https://www.youtube.com/watch?v=OSK6fzKEEfc",
      title: "Building Resilient Teams in Tech Companies",
      description: "Strategies for building resilient teams that can thrive in the fast-paced technology industry."
    },
    {
      url: "https://www.youtube.com/watch?v=MRE_Q0sLXtA",
      title: "Venture Capital and Startup Funding",
      description: "Insights into venture capital, startup funding, and how to secure investment for your business."
    },
    {
      url: "https://www.youtube.com/watch?v=DbGeTXH2cqw",
      title: "The Future of Work in a Digital Age",
      description: "Exploring how the nature of work is changing in the digital age and what it means for professionals."
    },
    {
      url: "https://www.youtube.com/watch?v=mE41rSGo6ns",
      title: "Cybersecurity Challenges in Financial Services",
      description: "Addressing the growing cybersecurity challenges facing the financial services industry today."
    },
    {
      url: "https://www.youtube.com/watch?v=Ld1xbxBm2_8",
      title: "Strategic Business Planning for Growth",
      description: "How to develop and implement strategic business plans that drive sustainable growth and success."
    },
    {
      url: "https://www.youtube.com/watch?v=bN-K6XgJU2U",
      title: "Personal Branding for Business Leaders",
      description: "The importance of personal branding for business leaders and how to build a strong professional presence."
    },
    {
      url: "https://www.youtube.com/watch?v=HDZIKMtqgoM",
      title: "Innovation Culture in Organizations",
      description: "How to foster a culture of innovation within organizations to drive creativity and growth."
    },
    {
      url: "https://www.youtube.com/watch?v=t4CGw72B8P4",
      title: "Financial Technology Trends for 2025",
      description: "Exploring the emerging trends in financial technology that will shape the industry in the coming years."
    },
    {
      url: "https://www.youtube.com/watch?v=fSf_EbfnqpE",
      title: "Risk Management in Digital Finance",
      description: "Strategies for effective risk management in the rapidly evolving digital finance landscape."
    },
    {
      url: "https://www.youtube.com/watch?v=O5CTNHpuuos",
      title: "Building Customer-Centric Financial Products",
      description: "How to develop financial products that truly address customer needs and create value."
    },
    {
      url: "https://www.youtube.com/watch?v=u7gD74vZI28",
      title: "Global Expansion Strategies for Tech Companies",
      description: "Insights into successful global expansion strategies for technology companies in competitive markets."
    },
    {
      url: "https://www.youtube.com/watch?v=wV4TugsaIcY",
      title: "The Intersection of AI and Finance",
      description: "Exploring the growing intersection of artificial intelligence and finance, and its implications for the future."
    },
    {
      url: "https://www.youtube.com/watch?v=z0u4ZKsoEFU",
      title: "Leadership Principles for Tech Entrepreneurs",
      description: "Essential leadership principles for technology entrepreneurs looking to build successful businesses."
    },
  ];

  // Create podcast objects with YouTube thumbnails and video titles/descriptions
  const podcasts = rawPodcasts.map((podcast, index) => {
    const videoId = getYouTubeID(podcast.url);
    
    return {
      id: index,
      title: podcast.title,
      url: podcast.url,
      image: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      description: podcast.description
    };
  });
  
  const activeVideo = podcasts[activeVideoId];

  const handlePodcastSelect = (id: number) => {
    if (id !== activeVideoId) {
      setIsLoading(true);
      setActiveVideoId(id);
      
      // Scroll to the video if on mobile
      if (window.innerWidth <= 1100 && videoRef.current) {
        videoRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleIframeLoad = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };
  
  // SEO metadata for Podcasts page
  const podcastsSeoData = {
    en: {
      title: 'Podcasts - Barkin Sayin | Business & Technology Insights',
      description: 'Listen to Barkin Sayin\'s podcasts covering fintech, entrepreneurship, digital transformation, and business innovation strategies across global markets.',
      keywords: [
        'Barkin Sayin podcast',
        'fintech podcasts',
        'entrepreneurship talks',
        'business innovation podcast',
        'smart TV technology',
        'digital transformation podcast',
        'global business strategies'
      ]
    },
    tr: {
      title: 'Podcast - Barkın Sayın | İş ve Teknoloji İçgörüleri',
      description: 'Barkın Sayın\'ın fintech, girişimcilik, dijital dönüşüm ve küresel pazarlarda iş inovasyonu stratejilerini kapsayan podcast\'lerini dinleyin.',
      keywords: [
        'Barkın Sayın podcast',
        'fintech podcast',
        'girişimcilik konuşmaları',
        'iş inovasyonu podcast',
        'akıllı TV teknolojisi',
        'dijital dönüşüm podcast',
        'küresel iş stratejileri'
      ]
    }
  };
  
  const seoTitle = podcastsSeoData[language as keyof typeof podcastsSeoData]?.title || podcastsSeoData.en.title;
  const seoDescription = podcastsSeoData[language as keyof typeof podcastsSeoData]?.description || podcastsSeoData.en.description;
  const seoKeywords = podcastsSeoData[language as keyof typeof podcastsSeoData]?.keywords || podcastsSeoData.en.keywords;

  return (
    <PodcastsContainer>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      
      <PageSection>
        <VideoBackground>
          <BackgroundPattern />
        </VideoBackground>
        
        <PageContainer>
          <PageHeader
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>{getText('pageTitle')}</PageTitle>
            <PageSubtitle>
              {getText('pageSubtitle')}
            </PageSubtitle>
          </PageHeader>
          
          <PlayerSection>
            <VideoContainer ref={videoRef}>
              <VideoFrame
                src={getYouTubeEmbedUrl(activeVideo.url)}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={handleIframeLoad}
                referrerPolicy="strict-origin-when-cross-origin"
                loading="lazy"
              />
              <VideoOverlay />
              <AnimatePresence mode="wait">
                {isLoading && (
                  <LoaderOverlay
                    key="loader"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                  >
                    <LoaderSpinnerContainer
                      animate={{ 
                        rotateZ: [0, 5, -5, 0],
                        transition: { 
                          repeat: Infinity, 
                          duration: 4,
                          ease: "easeInOut"
                        }
                      }}
                    >
                      <LoaderSpinner />
                      <LoaderSpinner />
                      <LoaderSpinner />
                    </LoaderSpinnerContainer>
                    <LoaderText
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      {getText('loadingVideo')}
                    </LoaderText>
                  </LoaderOverlay>
                )}
              </AnimatePresence>
              

            </VideoContainer>
            
            <PlaylistContainer
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <PlaylistHeader>
                <PlaylistTitle>{getText('allEpisodes')}</PlaylistTitle>
              </PlaylistHeader>
              
              <PlaylistBody>
                <PlaylistGrid
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {podcasts.map((podcast) => (
                    <PlaylistItem
                      key={podcast.id}
                      active={activeVideoId === podcast.id}
                      onClick={() => handlePodcastSelect(podcast.id)}
                      variants={itemVariants}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ItemThumbnail image={podcast.image}>
                        <PlayIcon
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </PlayIcon>
                      </ItemThumbnail>
                      <ItemInfo>
                        <ItemTitle>
                          {activeVideoId === podcast.id && (
                            <NowPlaying
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                            >
                              {getText('nowPlaying')}
                            </NowPlaying>
                          )}
                          {podcast.title}
                        </ItemTitle>
                        <ItemDescription>{podcast.description}</ItemDescription>
                      </ItemInfo>
                    </PlaylistItem>
                  ))}
                </PlaylistGrid>
              </PlaylistBody>
            </PlaylistContainer>
          </PlayerSection>
        </PageContainer>
      </PageSection>
    </PodcastsContainer>
  );
};

export default Podcasts; 